<script setup lang="ts">
import { useRouter } from 'vue-router';
import { AccountRouteName } from '@/js/account/app/router/route-names';
import BaseLoadState from '@/js/account/components/base/BaseLoadState.vue';
import DashboardQuickAction from '@/js/account/components/dashboard/DashboardQuickAction.vue';
import DocumentCard from '@/js/account/components/document/DocumentCard.vue';
import {
  useDocumentDownload,
  useRecentlyViewedDocuments,
} from '@/js/account/entities/document';
import { classNames } from '@/js/lib/bem';

const cn = classNames('profile-dashboard-customer');

const router = useRouter();
const { documents, loading, error } = useRecentlyViewedDocuments();
const { downloadingId, onDocumentDownload } = useDocumentDownload();

async function goToLibrary(): Promise<void> {
  await router.push({ name: AccountRouteName.Library });
}

async function goToDownloadHistory(): Promise<void> {
  await router.push({ name: AccountRouteName.Downloads });
}
</script>

<template>
  <div :class="cn()">
    <section :class="cn('documents')">
      <h2 :class="cn('documents-title')">
        Recently viewed policies
      </h2>

      <BaseLoadState
        :loading="loading"
        :error="error"
        :empty="documents.length === 0"
        empty-message="No recently viewed policies yet."
      >
        <div :class="cn('documents-list')">
          <DocumentCard
            v-for="document in documents"
            :key="document.id"
            :document="document"
            :downloading="downloadingId === document.id"
            @download="onDocumentDownload(document, $event)"
          />
        </div>
      </BaseLoadState>
    </section>

    <section :class="cn('quick-actions')">
      <h2 :class="cn('quick-actions-title')">
        Quick Actions
      </h2>
      <div :class="cn('quick-actions-list')">
        <DashboardQuickAction
          icon="book-open"
          title="Browse Library"
          description="Explore all available policy documents in the Base Library"
          button-label="Browse"
          @action="goToLibrary"
        />
        <DashboardQuickAction
          icon="search"
          title="Search Policies"
          description="Search for specific policies across the entire library."
          button-label="Search"
          @action="goToLibrary"
        />
        <DashboardQuickAction
          icon="download"
          title="View Download History"
          description="See the history of all downloaded policies and documents."
          button-label="View History"
          @action="goToDownloadHistory"
        />
      </div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.profile-dashboard-customer {
  display: grid;
  grid-template-columns: minmax(300px, 530px) minmax(300px, 567px);
  gap: 32px;

  &__documents {
    display: flex;
    flex-direction: column;
  }

  &__documents-title,
  &__quick-actions-title {
    font-size: 24px;
    font-weight: 400;
    line-height: 1.2;
    color: var(--color-text-primary);
    font-family: var(--font-family-serif);
    margin: 0 0 21px;
  }

  &__documents-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__quick-actions-list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  @media (max-width: 1246px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    &__quick-actions-list {
      grid-template-columns: 1fr;
    }
  }
}
</style>
